'use client';

import type { RegionAccommodationResponse } from '@weberus/shared-types';
import type { MouseEvent, FC } from 'react';
import type { RegionType } from './constants';
import { useRef, useState } from 'react';

import { cn } from '@yanolja-rnd/nol-design-system';
import { IcArrowforwardFill } from '@yanolja-rnd/nol-icons';
import { pTimeout } from '@weberus/utils';
import { clientFetcher } from '@/app/_shared/utils/fetcher/client-fetcher';
import { createRegionRecentSelected } from '../api';
import { REGION_TYPE_TO_PLACELIST_TYPE } from './constants';

interface RegionSelectAreaProps {
  targetType: string;
  regionItems: RegionAccommodationResponse['items'];
  regionType: RegionType;
}

export const RegionSelectArea: FC<RegionSelectAreaProps> = ({ targetType, regionItems, regionType }) => {
  const [selectedIdx, setSelectedIdx] = useState<number>(0);

  const rightAreaRef = useRef<HTMLDivElement>(null!);

  const rightList = regionItems?.[selectedIdx];

  const getLink = (region: RegionAccommodationResponse): string => {
    const placeListType = REGION_TYPE_TO_PLACELIST_TYPE[regionType];
    const placeListTypeRe = region.option?.accommodation?.topAdvertise ? 'top-adver' : placeListType;
    return `/${placeListTypeRe}/r-${region.code}`;
  };
  const handleItemClick = async (
    e: MouseEvent<HTMLAnchorElement>,
    {
      code,
      url,
      isAdver,
    }: {
      code: string;
      url: string;
      isAdver: boolean;
    },
  ) => {
    e.preventDefault();
    if (!isAdver) {
      // 야놀자 초이스는 최근 선택 지역에 추가하지 않음
      try {
        await pTimeout(createRegionRecentSelected(clientFetcher)({}, { regionCode: code, target: targetType }), 500);
      } catch (error) {
        /* empty */
      }
    }
    location.href = url;
  };

  return (
    <>
      {!!regionItems && regionItems.length > 0 && (
        <div className="mx-auto flex h-[calc(100vh-48px)] max-w-[600px] justify-center gap-16 px-20 pt-12 pc:w-pc-size pc:pt-20">
          <div className="shrink-0 overflow-y-auto">
            {regionItems.map((item, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  'block w-52 border-b border-b-fill-neutral-weak2 bg-fill-neutral-weak px-14 py-10 text-text-neutral-sub typography-body-12-regular first:rounded-t-8 last:rounded-b-8 last:border-b-0',
                  {
                    'text-bold bg-fill-neutral-main text-static-white': selectedIdx === index,
                  },
                )}
                onClick={() => {
                  setSelectedIdx(index);
                  rightAreaRef.current.scrollTo({ top: 0, behavior: 'instant' });
                }}>
                {item.title}
              </button>
            ))}
          </div>
          <div ref={rightAreaRef} className="w-full overflow-y-auto">
            {rightList?.items?.map((item) => (
              <>
                <a
                  href={getLink(item)}
                  className="sticky top-0 flex h-40 items-center justify-between rounded-8 border border-line-neutral-weak3 bg-static-white px-16"
                  onClick={(e) => {
                    handleItemClick(e, {
                      code: item.code!,
                      url: getLink(item),
                      isAdver: !!item.option?.accommodation?.topAdvertise,
                    });
                  }}>
                  <span className="typography-body-14-bold">{item.title}</span>
                  <div className="flex items-center">
                    <span className="typography-body-12-regular">{item.subTitle}</span>
                    <IcArrowforwardFill height={14} width={14} />
                  </div>
                </a>
                <div className="mb-18 mt-8 flex flex-col gap-3">
                  {item.items.map((subItem, subIndex) => (
                    <a
                      key={subIndex}
                      href={getLink(subItem)}
                      className="flex items-center px-16 py-11 text-text-neutral-main typography-body-14-regular"
                      onClick={(e) => {
                        handleItemClick(e, {
                          code: subItem.code!,
                          url: getLink(subItem),
                          isAdver: !!subItem.option?.accommodation?.topAdvertise,
                        });
                      }}>
                      {subItem.title}
                    </a>
                  ))}
                </div>
              </>
            ))}
            <div />
          </div>
        </div>
      )}
    </>
  );
};
